import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Copy,
  Edit,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import Dialog from "../components/ui/dialog";
import { mailcowAPI } from "../utils/api";
import useAuthStore from "../stores/authStore";

function generatePassword(len = 16) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint32Array(len);
  crypto.getRandomValues(bytes);
  let str = "";
  for (let i = 0; i < len; i++) {
    str += chars.charAt(bytes[i] % chars.length);
  }
  return str;
}

const emptyCreate = () => ({
  local_part: "",
  domain: "",
  password: "",
  password2: "",
  quota: "1024",
});

function validateCreateForm(form) {
  const errors = {};
  if (!form.local_part || !/^[A-Za-z0-9._-]+$/.test(form.local_part)) {
    errors.local_part =
      "ユーザー名は英数字・._- のみ使用できます（@ は含めないでください）";
  }
  if (!form.domain) {
    errors.domain = "ドメインを選択してください";
  }
  if (!form.password || form.password.length < 6) {
    errors.password = "パスワードは6文字以上で入力してください";
  }
  if (form.password !== form.password2) {
    errors.password2 = "パスワードが一致しません";
  }
  const quota = Number(form.quota);
  if (
    form.quota === "" ||
    !Number.isInteger(quota) ||
    quota < 0 ||
    quota > 10240
  ) {
    errors.quota =
      "クォータは 0～10240 の整数で指定してください（0 = 無制限）";
  }
  return errors;
}

function validateEditForm(form) {
  const errors = {};
  const quota = Number(form.quota);
  if (
    form.quota === "" ||
    !Number.isInteger(quota) ||
    quota < 0 ||
    quota > 10240
  ) {
    errors.quota =
      "クォータは 0～10240 の整数で指定してください（0 = 無制限）";
  }
  const hasPw = form.password || form.password2;
  if (hasPw) {
    if (!form.password || form.password.length < 6) {
      errors.password = "パスワードは6文字以上で入力してください";
    }
    if (form.password !== form.password2) {
      errors.password2 = "パスワードが一致しません";
    }
  }
  return errors;
}

const MailboxUsersPage = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === "admin";

  const [domains, setDomains] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate());
  const [createErrors, setCreateErrors] = useState({});
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [copiedCreatePassword, setCopiedCreatePassword] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editForm, setEditForm] = useState({
    quota: "1024",
    active: true,
    password: "",
    password2: "",
  });
  const [editErrors, setEditErrors] = useState({});
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [copiedEditPassword, setCopiedEditPassword] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [dRes, mRes] = await Promise.all([
        mailcowAPI.getDomains(),
        mailcowAPI.getMailboxes(
          domainFilter ? { domain: domainFilter } : {}
        ),
      ]);
      setDomains(dRes.data.domains || []);
      setMailboxes(mRes.data.mailboxes || []);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "メールユーザー一覧の取得に失敗しました"
      );
    } finally {
      setLoading(false);
    }
  }, [domainFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mailboxes;
    return mailboxes.filter((m) =>
      (m.email || "").toLowerCase().includes(q)
    );
  }, [mailboxes, search]);

  const openCreate = () => {
    setCreateForm({
      ...emptyCreate(),
      domain: domains[0] || "",
    });
    setCreateErrors({});
    setCreateError("");
    setCopiedCreatePassword(false);
    setCreateOpen(true);
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    const next = { ...createForm, [name]: value };
    setCreateForm(next);
    setCreateErrors(validateCreateForm(next));
  };

  const handleGenerateCreatePassword = () => {
    const pw = generatePassword(16);
    const next = { ...createForm, password: pw, password2: pw };
    setCreateForm(next);
    setCreateErrors(validateCreateForm(next));
    setCopiedCreatePassword(false);
  };

  const handleCopyCreatePassword = () => {
    if (!createForm.password) return;
    navigator.clipboard.writeText(createForm.password);
    setCopiedCreatePassword(true);
    setTimeout(() => setCopiedCreatePassword(false), 1000);
  };

  const handleCreateSave = async () => {
    const errors = validateCreateForm(createForm);
    setCreateErrors(errors);
    if (Object.keys(errors).length) return;
    setCreateLoading(true);
    setCreateError("");
    try {
      await mailcowAPI.createMailbox({
        local_part: createForm.local_part.trim(),
        domain: createForm.domain,
        password: createForm.password,
        password2: createForm.password2,
        quota: Number(createForm.quota),
      });
      setCreateOpen(false);
      fetchAll();
    } catch (err) {
      setCreateError(
        err.response?.data?.error || "メールユーザーの作成に失敗しました"
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (m) => {
    setEditEmail(m.email);
    setEditForm({
      quota: String(m.quota ?? 0),
      active: !!m.active,
      password: "",
      password2: "",
    });
    setEditErrors({});
    setEditError("");
    setCopiedEditPassword(false);
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = {
      ...editForm,
      [name]: type === "checkbox" ? checked : value,
    };
    setEditForm(next);
    setEditErrors(validateEditForm(next));
  };

  const handleGenerateEditPassword = () => {
    const pw = generatePassword(16);
    const next = { ...editForm, password: pw, password2: pw };
    setEditForm(next);
    setEditErrors(validateEditForm(next));
    setCopiedEditPassword(false);
  };

  const handleCopyEditPassword = () => {
    if (!editForm.password) return;
    navigator.clipboard.writeText(editForm.password);
    setCopiedEditPassword(true);
    setTimeout(() => setCopiedEditPassword(false), 1000);
  };

  const handleEditSave = async () => {
    const errors = validateEditForm(editForm);
    setEditErrors(errors);
    if (Object.keys(errors).length) return;
    setEditLoading(true);
    setEditError("");
    try {
      const payload = {
        quota: Number(editForm.quota),
        active: !!editForm.active,
      };
      if (editForm.password) {
        payload.password = editForm.password;
        payload.password2 = editForm.password2;
      }
      await mailcowAPI.updateMailbox(editEmail, payload);
      setEditOpen(false);
      fetchAll();
    } catch (err) {
      setEditError(
        err.response?.data?.error || "メールユーザーの更新に失敗しました"
      );
    } finally {
      setEditLoading(false);
    }
  };

  const openDelete = (email) => {
    setDeleteEmail(email);
    setDeleteError("");
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await mailcowAPI.deleteMailbox(deleteEmail);
      setDeleteOpen(false);
      fetchAll();
    } catch (err) {
      setDeleteError(
        err.response?.data?.error || "メールユーザーの削除に失敗しました"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatQuota = (q) => (Number(q) === 0 ? "∞" : `${q} MiB`);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2"
            onClick={() => navigate("/apps")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            アプリケーション管理へ
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">
            メールユーザー管理
          </h1>
          <p className="text-muted-foreground">
            mailcow 上のメールボックスを管理します
          </p>
        </div>
        <Button onClick={openCreate} disabled={!isAdmin}>
          <Plus className="h-4 w-4 mr-2" />
          メールユーザー追加
        </Button>
      </div>

      {!isAdmin && (
        <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
          管理者のみメールユーザーを変更できます。
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="メールアドレスで検索..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="border rounded px-3 py-2 text-sm"
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
            >
              <option value="">すべてのドメイン</option>
              {domains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={fetchAll} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-1" />
              更新
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>メールユーザー一覧</CardTitle>
          <CardDescription>
            {filtered.length} 件
            {domainFilter ? `（${domainFilter}）` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && <div className="text-red-600 mb-3 text-sm">{error}</div>}
          {loading ? (
            <p className="text-muted-foreground text-sm">読み込み中...</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              メールユーザーがありません
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">メールアドレス</th>
                    <th className="py-2 pr-4 font-medium">クォータ</th>
                    <th className="py-2 pr-4 font-medium">状態</th>
                    <th className="py-2 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr key={m.email} className="border-b last:border-0">
                      <td className="py-3 pr-4">{m.email}</td>
                      <td className="py-3 pr-4">{formatQuota(m.quota)}</td>
                      <td className="py-3 pr-4">
                        {m.active ? (
                          <span className="text-green-700">有効</span>
                        ) : (
                          <span className="text-gray-500">無効</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!isAdmin}
                            onClick={() => openEdit(m)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            disabled={!isAdmin}
                            onClick={() => openDelete(m.email)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="メールユーザー追加"
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={createLoading}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleCreateSave}
              disabled={
                createLoading ||
                !isAdmin ||
                Object.keys(createErrors).length > 0
              }
            >
              {createLoading ? "作成中..." : "作成"}
            </Button>
          </div>
        }
      >
        {createError && (
          <div className="text-red-600 mb-2 text-sm">{createError}</div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">ユーザー名</label>
            <Input
              name="local_part"
              value={createForm.local_part}
              onChange={handleCreateChange}
              placeholder="info"
            />
            {createErrors.local_part && (
              <div className="text-red-600 text-xs">{createErrors.local_part}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ドメイン</label>
            <select
              name="domain"
              className="w-full border rounded px-2 py-2 text-sm"
              value={createForm.domain}
              onChange={handleCreateChange}
            >
              <option value="">選択してください</option>
              {domains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {createErrors.domain && (
              <div className="text-red-600 text-xs">{createErrors.domain}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">パスワード</label>
            <div className="flex gap-2">
              <Input
                type="text"
                name="password"
                value={createForm.password}
                onChange={handleCreateChange}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateCreatePassword}
              >
                生成
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                disabled={!createForm.password}
                onClick={handleCopyCreatePassword}
              >
                {copiedCreatePassword ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            {createErrors.password && (
              <div className="text-red-600 text-xs">{createErrors.password}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              パスワード確認
            </label>
            <Input
              type="text"
              name="password2"
              value={createForm.password2}
              onChange={handleCreateChange}
            />
            {createErrors.password2 && (
              <div className="text-red-600 text-xs">{createErrors.password2}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              クォータ (MiB)
            </label>
            <Input
              name="quota"
              value={createForm.quota}
              onChange={handleCreateChange}
            />
            <p className="text-xs text-muted-foreground mt-1">
              max. 10240 MiB、0 = ∞
            </p>
            {createErrors.quota && (
              <div className="text-red-600 text-xs">{createErrors.quota}</div>
            )}
          </div>
        </div>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="メールユーザー編集"
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={editLoading}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={
                editLoading || !isAdmin || Object.keys(editErrors).length > 0
              }
            >
              {editLoading ? "保存中..." : "保存"}
            </Button>
          </div>
        }
      >
        {editError && (
          <div className="text-red-600 mb-2 text-sm">{editError}</div>
        )}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              メールアドレス
            </label>
            <Input value={editEmail} readOnly />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              クォータ (MiB)
            </label>
            <Input
              name="quota"
              value={editForm.quota}
              onChange={handleEditChange}
            />
            {editErrors.quota && (
              <div className="text-red-600 text-xs">{editErrors.quota}</div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="edit_active"
              name="active"
              checked={editForm.active}
              onChange={handleEditChange}
            />
            <label htmlFor="edit_active" className="text-sm">
              有効
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              新しいパスワード（変更する場合のみ）
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                name="password"
                value={editForm.password}
                onChange={handleEditChange}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGenerateEditPassword}
              >
                生成
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                disabled={!editForm.password}
                onClick={handleCopyEditPassword}
              >
                {copiedEditPassword ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            {editErrors.password && (
              <div className="text-red-600 text-xs">{editErrors.password}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              パスワード確認
            </label>
            <Input
              type="text"
              name="password2"
              value={editForm.password2}
              onChange={handleEditChange}
            />
            {editErrors.password2 && (
              <div className="text-red-600 text-xs">{editErrors.password2}</div>
            )}
          </div>
        </div>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="メールユーザー削除の確認"
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteLoading}
            >
              キャンセル
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading || !isAdmin}
            >
              {deleteLoading ? "削除中..." : "削除"}
            </Button>
          </div>
        }
      >
        {deleteError && (
          <div className="text-red-600 mb-2 text-sm">{deleteError}</div>
        )}
        <div>
          本当に <strong>{deleteEmail}</strong>{" "}
          を削除しますか？この操作は取り消せません。
        </div>
      </Dialog>
    </div>
  );
};

export default MailboxUsersPage;
